import { ErrorContentGenerator, ErrorContentMap } from "./getAlertContent";
import { MfaFields, MfaStepProps } from "../../components/SmartErrorAlert";
import { AxiosResponse } from "axios";
import { hasErrorMessage } from "../getApiErrorMessage";
import translate from "../translate";
import { MfaEnrollStep1Response, MfaEnrollStep2Response } from "../../../mfa/hooks/useMfaApi";

export type MfaEnrollStep1ErrorState = "INVALID_MFA_VALUE" | "GOV_DELIVERY_ERROR" | "MFA_CHANNEL_EXCEEDS_USE_LIMIT" | "MFA_SERVER_ERROR" | "MFA_TOO_MANY_ATTEMPTS" | "GOV_DELIVERY_ADD_ERROR" | "GOV_DELIVERY_EDIT_ERROR" | "GOV_DELIVERY_ADD_GET_NEW_CODE_ERROR" | "GOV_DELIVERY_EDIT_GET_NEW_CODE_ERROR" | "GOV_DELIVERY_ERROR_ANOTHER_CODE";

export const serviceError = {
    header: translate("cant_process_request_time"),
    body: <>
        <p className="ds-u-margin-top--0 ds-u-margin-bottom--0">{translate("try_setup_later")}</p>
        <a href={'/my/home'}>{translate("go_back_home")}</a>
    </>,
    analyticsErr: "We can't process your request at this time. Try setting up 2-step verification later.Go back to account home."
};

export const govDeliveryErrorContent = {
    header: translate("cant_send_code"),
    body: serviceError.body,
    analyticsErr: "We can't send your security code right now. Try setting up 2-step verification later.Go back to account home."
}

export const govDeliveryErrorAnotherCodeContent = {
    header: translate("we_couldnt_send_another_code"),
    body: serviceError.body,
    analyticsErr: "We can't send another security code right now. Try setting up 2-step verification later.Go back to account home."
}

export const govDeliveryErrorContentAddFlow = {
    header: translate("cant_send_code"),
    body: <>
        <p className="ds-u-margin-top--0 ds-u-margin-bottom--0">{translate("try_adding_another_method")}</p>
        <a href={'/my/home'}>{translate("go_back_home")}</a>
    </>,
    analyticsErr: "We can't send your security code right now. Try adding another method later.Go back to account home."
}

export const govDeliveryErrorContentAddAnotherCodeFlow = {
    header: translate("we_couldnt_send_another_code"),
    body: <>
        <p className="ds-u-margin-top--0 ds-u-margin-bottom--0">{translate("try_adding_another_method")}</p>
        <a href={'/my/home'}>{translate("go_back_home")}</a>
    </>,
    analyticsErr: "We can't send another security code right now. Try adding another method later.Go back to account home."
}

export const govDeliveryErrorContentEditFlow = {
    header: translate("cant_send_code"),
    body: <>
        <p className="ds-u-margin-top--0 ds-u-margin-bottom--0">{translate("try_editing_another_method")}</p>
        <a href={'/my/home'}>{translate("go_back_home")}</a>
    </>,
    analyticsErr: "We can't send your security code right now. Try editing your 2-step verification later.Go back to account home."
}

export const govDeliveryErrorContentEditAnotherCodeFlow = {
    header: translate("we_couldnt_send_another_code"),
    body: <>
        <p className="ds-u-margin-top--0 ds-u-margin-bottom--0">{translate("try_editing_another_method")}</p>
        <a href={'/my/home'}>{translate("go_back_home")}</a>
    </>,
    analyticsErr: "We can't send another security code right now. Try editing your 2-step verification later.Go back to account home."
}

export const apiErrorContentVoice: ErrorContentMap<MfaEnrollStep1ErrorState> = {
    INVALID_MFA_VALUE: {
        header: translate("cant_use_phone"),
        body: translate("invalid_phone_number_voice"),
        analyticsErr: "You can't use this phone number. The phone number you entered is invalid. Enter a 10-digit phone number and try again."
    },
    GOV_DELIVERY_ERROR: govDeliveryErrorContent,
    GOV_DELIVERY_ERROR_ANOTHER_CODE: govDeliveryErrorAnotherCodeContent,
    GOV_DELIVERY_ADD_ERROR: govDeliveryErrorContentAddFlow,
    GOV_DELIVERY_ADD_GET_NEW_CODE_ERROR: govDeliveryErrorContentAddAnotherCodeFlow,
    GOV_DELIVERY_EDIT_ERROR: govDeliveryErrorContentEditFlow,
    GOV_DELIVERY_EDIT_GET_NEW_CODE_ERROR: govDeliveryErrorContentEditAnotherCodeFlow,
    MFA_CHANNEL_EXCEEDS_USE_LIMIT: {
        header: translate("cant_use_phone"),
        body: translate("alread_used_mfa"),
        analyticsErr: "You can't use this phone number. It is already being used by multiple accounts. Enter a different one."
    },
    MFA_SERVER_ERROR: serviceError,
    MFA_TOO_MANY_ATTEMPTS: serviceError
}

export const step1ErrFnsVoice = new ErrorContentGenerator<MfaStepProps, MfaFields, MfaEnrollStep1ErrorState>({
    apiErrorContent: apiErrorContentVoice,
    invalidFieldHeader: "phone_number", // No validation alerts for this step, so this isn't actually used
    getRefMap: props => {
        return {
            field: props.fieldRef
        }
    },
    getErrState,
    invalidFieldMessages: { // No validation alerts for this step, so this isn't actually used
        field: {
            linkText: 'phone_number',
            messages: {empty: 'phone_number'}
        }
    },
    getApiInvalidFields: () => [],
    isIntermediateStep: false
});

const apiErrorContentSms: ErrorContentMap<MfaEnrollStep1ErrorState> = {
    INVALID_MFA_VALUE: {
        header: translate("cant_use_mobile"),
        body: translate("invalid_phone_number_sms"),
        analyticsErr: "You can't use this phone number. The mobile phone number you entered is invalid. Enter a 10-digit mobile phone number and try again."
    },
    GOV_DELIVERY_ERROR: govDeliveryErrorContent,
    GOV_DELIVERY_ERROR_ANOTHER_CODE: govDeliveryErrorAnotherCodeContent,
    GOV_DELIVERY_ADD_ERROR: govDeliveryErrorContentAddFlow,
    GOV_DELIVERY_ADD_GET_NEW_CODE_ERROR: govDeliveryErrorContentAddAnotherCodeFlow,
    GOV_DELIVERY_EDIT_ERROR: govDeliveryErrorContentEditFlow,
    GOV_DELIVERY_EDIT_GET_NEW_CODE_ERROR: govDeliveryErrorContentEditAnotherCodeFlow,
    MFA_CHANNEL_EXCEEDS_USE_LIMIT: {
        header: translate("cant_use_mobile"),
        body: translate("alread_used_mfa"),
        analyticsErr: "You can't use this phone number. It is already being used by multiple accounts. Enter a different one."
    },
    MFA_SERVER_ERROR: serviceError,
    MFA_TOO_MANY_ATTEMPTS: serviceError
}

export const step1ErrFnsSms = new ErrorContentGenerator<MfaStepProps, MfaFields, MfaEnrollStep1ErrorState>({
    apiErrorContent: apiErrorContentSms,
    invalidFieldHeader: "phone_number", // No validation alerts for this step, so this isn't actually used
    getRefMap: props => {
        return {
            field: props.fieldRef
        }
    },
    getErrState,
    invalidFieldMessages: { // No validation alerts for this step, so this isn't actually used
        field: {
            linkText: 'phone_number',
            messages: {empty: 'phone_number'}
        }
    },
    getApiInvalidFields: () => [],
    isIntermediateStep: false
});

const apiErrorContentEmail: ErrorContentMap<MfaEnrollStep1ErrorState> = {
    INVALID_MFA_VALUE: {
        header: translate("cant_use_email"),
        body: translate("the_email_is_invalid"),
        analyticsErr: "You can't use this email address. The email you entered is invalid. Check the information your entered and try again."
    },
    GOV_DELIVERY_ERROR: govDeliveryErrorContent,
    GOV_DELIVERY_ERROR_ANOTHER_CODE: govDeliveryErrorAnotherCodeContent,
    GOV_DELIVERY_ADD_ERROR: govDeliveryErrorContentAddFlow,
    GOV_DELIVERY_ADD_GET_NEW_CODE_ERROR: govDeliveryErrorContentAddAnotherCodeFlow,
    GOV_DELIVERY_EDIT_ERROR: govDeliveryErrorContentEditFlow,
    GOV_DELIVERY_EDIT_GET_NEW_CODE_ERROR: govDeliveryErrorContentEditAnotherCodeFlow,
    MFA_CHANNEL_EXCEEDS_USE_LIMIT: {
        header: translate("cant_use_email"),
        body: translate("alread_used_mfa"),
        analyticsErr: "You can't use this email address. It is already being used by multiple accounts. Enter a different one."
    },
    MFA_SERVER_ERROR: serviceError,
    MFA_TOO_MANY_ATTEMPTS: serviceError
}

export const step1ErrFnsEmail = new ErrorContentGenerator<MfaStepProps, MfaFields, MfaEnrollStep1ErrorState>({
    apiErrorContent: apiErrorContentEmail,
    invalidFieldHeader: "email", // No validation alerts for this step, so this isn't actually used
    getRefMap: props => {
        return {
            field: props.fieldRef
        }
    },
    getErrState,
    invalidFieldMessages: { // No validation alerts for this step, so this isn't actually used
        field: {
            linkText: 'email',
            messages: {empty: 'email'}
        }
    },
    getApiInvalidFields: () => [],
    isIntermediateStep: false
});

function getErrState(apiResponse: AxiosResponse<MfaEnrollStep1Response | MfaEnrollStep2Response> | null, extraData?: any) {
    if (!apiResponse || !apiResponse.data.errors || apiResponse.data.code === 401) {
        return null;
    }
    const { mfaFlow, mfaIsAnotherCodeRequested } = extraData;
    switch (apiResponse.data.code) {
        case 400:
            if(hasErrorMessage(apiResponse.data.errors, "INVALID_MFA_VALUE")) {
                return "INVALID_MFA_VALUE";
            }
            if(hasErrorMessage(apiResponse.data.errors, "GOV_DELIVERY_ERROR")) {
                if (mfaFlow === 'add') {
                    if (mfaIsAnotherCodeRequested) {
                        return 'GOV_DELIVERY_ADD_GET_NEW_CODE_ERROR';
                    } else {
                        return 'GOV_DELIVERY_ADD_ERROR';
                    }
                } else if (mfaFlow === 'edit' ) {
                    if (mfaIsAnotherCodeRequested) {
                        return 'GOV_DELIVERY_EDIT_GET_NEW_CODE_ERROR';
                    } else {
                        return 'GOV_DELIVERY_EDIT_ERROR';
                    }
                } else if (mfaIsAnotherCodeRequested) {
                    return "GOV_DELIVERY_ERROR_ANOTHER_CODE"
                }
                return "GOV_DELIVERY_ERROR";
            }
            if (hasErrorMessage(apiResponse.data.errors, "MFA_TOO_MANY_ATTEMPTS")) {
                return "MFA_TOO_MANY_ATTEMPTS";
            }
            if (hasErrorMessage(apiResponse.data.errors, 'MFA_NON_US_PHONE_NUMBER')) {
                return null;
            }
            return "MFA_SERVER_ERROR";
        case 409:
            if (hasErrorMessage(apiResponse.data.errors, 'MFA_DUPLICATE_CHANNEL')) {
                return null; // user attempting to re-use their own channel - inline error
            } else {
                return "MFA_CHANNEL_EXCEEDS_USE_LIMIT";
            }
        default:
            return "MFA_SERVER_ERROR";
    }
}